import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './aggregatedCoordinates.Renderer';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import {
  calculateCenter,
  getCenterDisplayName,
  getCenterHoverText,
  getWhiskerDisplayName,
  getWhiskerHoverText,
} from './rendererOptions';

export function AggregatedCoordinates({
  chartTypeManifest,
  dataContext,
  dataBinding,
  config,
  widgetContext,
}: TpChartProps) {
  const widgetGlobalConfig = widgetContext.dashboardContext.widgetGlobalConfig ?? {};
  const {
    definition,
    aggregates,
    coordinates,
    hasCategories,
    categoriesColInfo,
    areaLines,
    displayType,
    customHoverRenderer,
    title,
    getXAxisCenter,
    mapPixelGapToLogicalGap,
  } = createDefinition(chartTypeManifest, dataContext, dataBinding, config, widgetGlobalConfig);

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (mappingInfo.area?.id != 'lines') return null;
    if (coordinates.length === 0) return null;

    const itemId = null; // We are displaying aggregates, not items: always null
    let minDist = mappingInfo.mapsize(10);
    let coordIdx = null;
    let hoverCentX = null;
    let hoverCentY = null;

    // Find coordinate axis for the hover position based on logicPositX (which is between 0 and 1)
    const boundLogicX = Math.max(0, Math.min(1, logicPositX));
    const coordinateLoc = Math.min(Math.trunc(boundLogicX * coordinates.length), coordinates.length - 1);
    let hoverCategoryId = null;
    let hoverSelected = null;

    const logicalGap = mapPixelGapToLogicalGap(mappingInfo.mapX.map, mappingInfo.mapX.mapInv, mappingInfo.mapsize);

    // First iterate over unselected, then selected as the last one wins
    for (const isSelected of [false, true]) {
      for (const [categoryId, aggregate] of aggregates) {
        const coordinateInfo = aggregate.coordinates[coordinateLoc];
        const kernel = isSelected ? coordinateInfo.densitySelected : coordinateInfo.density;
        const value = calculateCenter(kernel, displayType);
        if (!isNaN(value)) {
          const hoverCenterX = getXAxisCenter(coordinateLoc, categoryId, logicalGap);
          const scr_x = mappingInfo.mapX.map(hoverCenterX);
          const scr_y = mappingInfo.mapY.map(value);
          const dst = Math.abs(scr_x - renderPositX) + Math.abs(scr_y - renderPositY);
          if (dst <= minDist) {
            minDist = dst;
            hoverCategoryId = categoryId;
            hoverCentX = hoverCenterX;
            hoverCentY = value;
            coordIdx = coordinateLoc;
            hoverSelected = isSelected;
          }
        }
      }
    }
    return {
      dataTableId: dataBinding.dataTableId,
      itemId,
      custom: { hoverCentX, hoverCentY, coordIdx, categoryId: hoverCategoryId, isSelected: hoverSelected },
    };
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId || hoverInfo.catColumnId) return null;
    if (hoverInfo?.custom?.categoryId == null) return null;

    const categoryId = hoverInfo.custom.categoryId;
    const aggregate = aggregates.get(categoryId);
    const coordIdx = hoverInfo.custom.coordIdx;
    const isSelected = hoverInfo.custom.isSelected;

    if (isSelected == null || coordIdx == null || aggregate == null) return null;

    const labels: string[] = [];
    if (context.isActiveChart) {
      const coordinateInfo = aggregate.coordinates[coordIdx];
      labels.push(coordinateInfo.columnInfo.name); // Column name
      if (hasCategories) {
        // Category
        labels.push(`${categoriesColInfo.name}=${convertColumnDataToDisplayString(categoriesColInfo, categoryId)}`);
      }
      const kernel = isSelected ? coordinateInfo.densitySelected : coordinateInfo.density;

      // Display the configured center metric in the hover message based on the current display type.
      const centerValueType = getCenterDisplayName(displayType);
      const centerValue = getCenterHoverText(coordinateInfo.columnInfo, kernel, displayType);
      labels.push(`${centerValueType} ${isSelected ? '(selected)' : '(total)'}: ${centerValue}`);

      // We also display the whisker values for the kernelDF in the hover message
      if (config.whiskers !== 'none') {
        const whiskersValueType = getWhiskerDisplayName(config.whiskers);
        const whiskerValueDisplay = getWhiskerHoverText(coordinateInfo.columnInfo, kernel, config.whiskers);
        labels.push(`${whiskersValueType} ${isSelected ? '(selected)' : '(total)'}: ${whiskerValueDisplay}`);
      }
    }
    return {
      area: areaLines,
      logicPosX: context.isActiveChart ? hoverInfo.custom?.hoverCentX : null,
      logicPosY: context.isActiveChart ? hoverInfo.custom?.hoverCentY : null,
      labels,
      customHoverRenderer,
    };
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        maxZoomXRelative={30}
        maxZoomYRelative={30}
        widgetContext={widgetContext}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
    </div>
  );
}
